<template>
  <v-card class="chat-conv-card bg-surface rounded-xl border border-white-05 overflow-hidden" elevation="12">
    <div class="d-flex align-center pa-4 border-b">
      <v-btn icon="mdi-arrow-left" variant="text" size="small" class="mr-2" @click="$emit('back')"></v-btn>
      <div class="d-flex align-center cursor-pointer flex-grow-1 overflow-hidden" @click="$emit('view-profile', peer?.handle)">
        <v-avatar size="40" class="mr-3 border border-white-05">
          <v-img v-if="peer?.avatar" :src="peer.avatar"></v-img>
          <v-icon v-else icon="mdi-account"></v-icon>
        </v-avatar>
        <div class="d-flex flex-column overflow-hidden">
          <span class="text-body-3 font-weight-black line-height-1 text-truncate">{{ peer?.displayName || peer?.handle }}</span>
          <span class="text-caption text-grey text-truncate">@{{ peer?.handle }}</span>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon="mdi-refresh" variant="text" size="small" :loading="loadingMessages" @click="fetchMessages()"></v-btn>
    </div>

    <div ref="messageContainer" class="message-history pa-4 flex-grow-1 overflow-y-auto">
      <div v-if="loadingMessages && !messages.length" class="text-center py-10">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
      </div>

      <div v-else class="d-flex flex-column gap-2">
        <template v-for="(msg, index) in messages" :key="msg.id || index">
          <!-- Date Pill -->
          <div v-if="isNewDay(msg, messages[index - 1])" class="d-flex justify-center my-4">
            <div class="date-pill px-4 py-1 rounded-pill bg-black-05 border border-white-05 text-tiny text-grey font-weight-black">
              {{ formatDateHeader(msg.sentAt) }}
            </div>
          </div>

          <div :class="['d-flex mb-1', isMine(msg) ? 'justify-end' : 'justify-start']">
            <div :class="['message-bubble pa-3 rounded-lg max-w-75', isMine(msg) ? 'bg-primary text-white' : 'bg-grey-darken-4 text-white']">
              <div class="text-body-2" v-html="renderRichText(msg.text, msg.facets)" @click="onChatTextClick"></div>
              <div :class="['text-tiny mt-1', isMine(msg) ? 'text-white-70 text-right' : 'text-grey']">
                {{ formatTime(msg.sentAt) }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="pa-4 border-t">
      <div class="d-flex align-center gap-2">
        <v-textarea
          v-model="newMessage"
          placeholder="Write a message..."
          rows="1"
          auto-grow
          density="comfortable"
          variant="solo-filled"
          flat
          hide-details
          class="chat-input rounded-xl"
          @keydown.enter.exact.prevent="send"
        ></v-textarea>
        <v-btn
          icon="mdi-send"
          color="primary"
          :disabled="!newMessage.trim() || sending"
          :loading="sending"
          elevation="0"
          @click="send"
        ></v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useRouter } from 'vue-router';
import { getConversationMessages, sendMessageToConvo, updateChatRead, currentUser, showAlert } from '@/services/bluesky';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';

const props = defineProps<{
  convo: any;
}>();

const emit = defineEmits(['back', 'read', 'view-profile']);

const messages = ref<any[]>([]);
const loadingMessages = ref(false);
const newMessage = ref('');
const sending = ref(false);
const messageContainer = ref<HTMLElement | null>(null);
const router = useRouter();
let pollInterval: any = null;

const peer = computed(() => {
  return props.convo.members?.find((m: any) => m.did !== currentUser.value?.did);
});

const isMine = (msg: any) => {
  return msg.sender?.did === currentUser.value?.did;
};

const fetchMessages = async () => {
  loadingMessages.value = true;
  try {
    const res = await getConversationMessages(props.convo.id, 50);
    // Reverse to show oldest first
    messages.value = res.messages.reverse();
    scrollToBottom();
    
    // Mark as read
    if (messages.value.length > 0) {
      await updateChatRead(props.convo.id, messages.value[messages.value.length - 1].id);
      emit('read');
    }
  } finally {
    loadingMessages.value = false;
  }
};

const pollMessages = async () => {
  try {
    const res = await getConversationMessages(props.convo.id, 20);
    const newMessages = res.messages.reverse();
    
    // Check if we have new messages
    if (newMessages.length > 0 && newMessages[newMessages.length - 1].sentAt !== messages.value[messages.value.length - 1]?.sentAt) {
      messages.value = newMessages;
      scrollToBottom();
      
      // Mark as read
      await updateChatRead(props.convo.id, messages.value[messages.value.length - 1].id);
      emit('read');
    }
  } catch (e) {
    console.error('Polling error:', e);
  }
};

const send = async () => {
  if (!newMessage.value.trim() || sending.value) return;
  
  const text = newMessage.value.trim();
  sending.value = true;
  try {
    await sendMessageToConvo(props.convo.id, text);
    newMessage.value = '';
    await fetchMessages();
  } catch (e) {
    await showAlert('Failed to send message');
  } finally {
    sending.value = false;
  }
};

const onChatTextClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router,
    onTagClick: (tag) => {
      // For chat, we might just open bsky search or navigate if we have a tag view
      window.open(`https://bsky.app/search?q=%23${tag}`, '_blank');
    }
  });
};

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

const formatTime = (time: string) => {
  if (!time) return '';
  return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isNewDay = (msg: any, prevMsg: any) => {
  if (!msg?.sentAt) return false;
  if (!prevMsg) return true; // First message of the conversation
  
  const d1 = new Date(msg.sentAt).toDateString();
  const d2 = new Date(prevMsg.sentAt).toDateString();
  return d1 !== d2;
};

const formatDateHeader = (time: string) => {
  const date = new Date(time);
  const now = new Date();
  
  if (date.toDateString() === now.toDateString()) return 'Today';
  
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const messageLineHeight = { lineHeight: 1 };

onMounted(() => {
  fetchMessages();
  pollInterval = setInterval(pollMessages, 3000);
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
});
</script>

<style scoped>
.chat-conv-card {
  width: 360px;
  max-width: 90vw;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.message-history {
  display: flex;
  flex-direction: column;
  background: rgba(var(--v-theme-surface), 0.5);
}

.message-bubble {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  word-break: break-word;
}

.max-w-75 {
  max-width: 75%;
}

.text-tiny {
  font-size: 0.65rem;
}

.text-white-70 {
  color: rgba(255, 255, 255, 0.7);
}

.line-height-1 {
  line-height: 1.2;
}

.bg-black-05 {
  background: rgba(255, 255, 255, 0.03) !important;
}

.date-pill {
  text-transform: uppercase;
  letter-spacing: 1px;
  backdrop-filter: blur(8px);
}

.border-b {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.border-t {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.gap-2 {
  gap: 8px;
}

.chat-input :deep(.v-field__input) {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.message-bubble :deep(a) {
  color: inherit;
  text-decoration: underline;
  font-weight: bold;
}

.bg-primary :deep(a) {
  color: #fff !important;
  opacity: 0.9;
}

.bg-primary :deep(a:hover) {
  opacity: 1;
}
</style>
